import { useMemo, useRef, useState } from 'react'
import { ArrowRepeat, Upload, CheckCircleFill, ExclamationCircle } from 'react-bootstrap-icons'
import { useData } from '../store.jsx'
import { PageHeader, Panel, Stat, CommitInput } from '../components/ui.jsx'
import { entries } from '../lib/finance.js'
import { money, number, dateLabel, todayISO } from '../lib/format.js'
import { parseLightyear, summarizeLightyear } from '../lib/lightyear.js'
import { updateAt, syncCoinbase } from '../lib/firebase.js'

export default function Investments() {
  const data = useData()
  const linked = (src) => entries(data.accounts).filter((a) => a.source === src)
  return (
    <>
      <PageHeader title="Lightyear & Coinbase" subtitle="Link an account to a source on the Balances page to use these values in your net worth." />
      <div className="row g-3">
        <div className="col-12"><Coinbase cb={data.integrations?.coinbase} linked={linked('coinbase')} /></div>
        <div className="col-12"><Lightyear ly={data.integrations?.lightyear} linked={linked('lightyear')} /></div>
      </div>
    </>
  )
}

function LinkedHint({ linked }) {
  return linked.length
    ? <span className="small text-success"><CheckCircleFill /> Used by: {linked.map((a) => a.name).join(', ')}</span>
    : <span className="small text-body-secondary">Not linked to any account yet</span>
}

function Coinbase({ cb, linked }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  async function sync() {
    setBusy(true); setErr(null)
    try { await syncCoinbase() } catch (e) { setErr(e.message) }
    setBusy(false)
  }
  const accounts = (cb?.accounts || []).filter((a) => a.native > 0.005).sort((a, b) => b.native - a.native)
  return (
    <Panel title={<span>Coinbase <span className="ms-2"><LinkedHint linked={linked} /></span></span>}
      action={<button className="btn btn-sm btn-primary" onClick={sync} disabled={busy}><ArrowRepeat className={busy ? 'spin' : ''} /> {busy ? 'Syncing…' : 'Sync now'}</button>}>
      {(err || cb?.error) && <div className="alert alert-danger py-2 small"><ExclamationCircle /> {err || cb.error}</div>}
      {cb?.syncedAt ? (
        <>
          <div className="row g-3 mb-3">
            <div className="col-6 col-lg-3"><Stat label="Total" value={money(cb.total, { currency: cb.currency })} hint={`Synced ${new Date(cb.syncedAt).toLocaleString('en-GB')}`} /></div>
            <div className="col-6 col-lg-3"><Stat label="Assets" value={accounts.length} /></div>
          </div>
          <div className="table-wrap"><table className="table table-sm">
            <thead><tr><th>Asset</th><th className="text-end">Amount</th><th className="text-end">Value</th><th className="text-end">Share</th></tr></thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id || a.currency}><td>{a.name || a.currency} <span className="text-body-secondary small">{a.currency}</span></td>
                  <td className="num">{number(a.balance, 8)}</td><td className="num">{money(a.native, { currency: cb.currency, digits: 2 })}</td>
                  <td className="num text-body-secondary">{cb.total ? Math.round((a.native / cb.total) * 100) : 0}%</td></tr>
              ))}
            </tbody>
          </table></div>
        </>
      ) : (
        <div className="small text-body-secondary">
          Not synced yet. Setup (one time):
          <ol className="mt-2 mb-0">
            <li>At <a href="https://portal.cdp.coinbase.com/access/api" target="_blank" rel="noreferrer">portal.cdp.coinbase.com</a>, create a <b>Secret API key</b>: algorithm <b>ECDSA</b>, permission <b>View</b> only.</li>
            <li>Store it as function secrets: <code>firebase functions:secrets:set COINBASE_KEY_NAME</code> and <code>COINBASE_KEY_SECRET</code>.</li>
            <li>Deploy functions (<code>firebase deploy --only functions</code>, needs the Blaze plan), then press <b>Sync now</b>. It also syncs automatically every morning.</li>
          </ol>
        </div>
      )}
    </Panel>
  )
}

const rowKey = (t) => `${t.date}_${t.type}_${t.isin || t.ticker}_${t.quantity}_${t.total}`.replace(/[.#$/[\]]/g, '-')

function Lightyear({ ly, linked }) {
  const fileRef = useRef()
  const [msg, setMsg] = useState(null)
  const tx = useMemo(() => Object.values(ly?.transactions || {}).sort((a, b) => a.date.localeCompare(b.date)), [ly?.transactions])
  const summary = useMemo(() => summarizeLightyear(tx), [tx])
  const deposited = summary.deposits.EUR || 0

  async function onFile(e) {
    const file = e.target.files[0]
    e.target.value = ''
    if (!file) return
    try {
      const rows = parseLightyear(await file.text())
      const existing = ly?.transactions || {}
      const patch = {}
      for (const t of rows) { const k = rowKey(t); if (!existing[k]) patch[`transactions/${k}`] = t }
      await updateAt('integrations/lightyear', { ...patch, importedAt: Date.now(), fileName: file.name })
      setMsg({ ok: true, text: `Imported ${Object.keys(patch).length} new of ${rows.length} rows.` })
    } catch (err) { setMsg({ ok: false, text: err.message }) }
  }

  return (
    <Panel title={<span>Lightyear <span className="ms-2"><LinkedHint linked={linked} /></span></span>}
      action={<>
        <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onFile} />
        <button className="btn btn-sm btn-primary" onClick={() => fileRef.current.click()}><Upload /> Import CSV</button>
      </>}>
      {msg && <div className={`alert py-2 small ${msg.ok ? 'alert-success' : 'alert-danger'}`}>{msg.text}</div>}
      <p className="small text-body-secondary">
        Lightyear has no personal API. In the app go to <b>Profile → Reports → Transaction report</b>, export CSV and import it here.
        Re-importing is safe: duplicates are skipped.
      </p>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <div className="stat h-100">
            <div className="label">Current portfolio value (EUR)</div>
            <CommitInput className="form-control form-control-lg fw-bold mt-1" value={ly?.value} onSave={(v) => updateAt('integrations/lightyear', { value: v, valueUpdatedAt: todayISO() })} />
            <div className="hint">{ly?.valueUpdatedAt ? `Updated ${dateLabel(ly.valueUpdatedAt)}` : 'Copy it from the Lightyear app'}</div>
          </div>
        </div>
        <div className="col-6 col-md-4"><Stat label="Net deposited" value={money(deposited)} hint={tx.length ? `${tx.length} transactions` : 'Import a CSV'} /></div>
        <div className="col-6 col-md-4"><Stat label="Gain" value={ly?.value && deposited ? money(ly.value - deposited, { sign: true }) : '–'}
          tone={ly?.value && deposited ? (ly.value - deposited < 0 ? 'neg' : 'pos') : ''} hint={ly?.value && deposited ? `${number(((ly.value - deposited) / deposited) * 100, 1)}%` : null} /></div>
      </div>
      {summary.holdings.length > 0 && (
        <div className="table-wrap"><table className="table table-sm">
          <thead><tr><th>Holding</th><th className="text-end">Quantity</th><th className="text-end">Avg cost</th><th className="text-end">Invested</th></tr></thead>
          <tbody>
            {summary.holdings.map((h) => (
              <tr key={h.isin || h.ticker}><td>{h.ticker} <span className="text-body-secondary small">{h.isin}</span></td>
                <td className="num">{number(h.quantity, 6)}</td>
                <td className="num">{money(h.avgCost, { currency: h.currency, digits: 2 })}</td>
                <td className="num">{money(h.cost, { currency: h.currency })}</td></tr>
            ))}
          </tbody>
        </table></div>
      )}
      {Object.keys(summary.dividends).length > 0 && (
        <div className="small text-body-secondary mt-2">Dividends (net of tax): {Object.entries(summary.dividends).map(([c, v]) => money(v, { currency: c, digits: 2 })).join(' · ')}</div>
      )}
    </Panel>
  )
}
